/**
 * PF-32: Dependency-free replacement for Kendo Calendar (cart delivery date picker).
 * Exposes the same jQuery-plugin surface the theme uses:
 *   $(el).kendoCalendar({ value, min, max, change, disableDates })
 *   $(el).data('kendoCalendar') -> { value(), element, destroy() }
 * Markup mirrors Kendo's class names (k-calendar / k-calendar-view / k-calendar-td /
 * k-link / k-today / k-selected / k-disabled) so the section's existing CSS and
 * click hooks keep working. Replaces kendo.all.min.js (5.7MB) + theme CSS (700KB).
 */
(function ($) {
	'use strict';

	if (!$ || $.fn.kendoCalendar) {
		return;
	}

	var WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
	var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'];

	function atMidnight(d) {
		var x = new Date(d);
		x.setHours(0, 0, 0, 0);
		return x;
	}

	function sameDay(a, b) {
		return a && b && a.getFullYear() === b.getFullYear() &&
			a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
	}

	function Calendar($el, opts) {
		this.$el = $el;
		this.element = $el;
		this.opts = opts || {};
		this._value = this.opts.value ? atMidnight(this.opts.value) : null;
		this._min = this.opts.min ? atMidnight(this.opts.min) : null;
		this._max = this.opts.max ? atMidnight(this.opts.max) : null;
		var base = this._value || this._min || new Date();
		this._viewYear = base.getFullYear();
		this._viewMonth = base.getMonth();
		this.render();
	}

	Calendar.prototype.isDisabled = function (date) {
		var d = atMidnight(date);
		if (this._min && d < this._min) return true;
		if (this._max && d > this._max) return true;
		if (typeof this.opts.disableDates === 'function') {
			try {
				if (this.opts.disableDates(new Date(d))) return true;
			} catch (e) { /* treat probe errors as enabled */ }
		}
		return false;
	};

	Calendar.prototype.value = function (v) {
		if (typeof v === 'undefined') {
			return this._value ? new Date(this._value) : null;
		}
		this._value = v ? atMidnight(v) : null;
		if (this._value) {
			this._viewYear = this._value.getFullYear();
			this._viewMonth = this._value.getMonth();
		}
		this.render(); // programmatic set: silent, like Kendo
	};

	Calendar.prototype.navigate = function (delta) {
		var m = this._viewMonth + delta;
		this._viewYear += Math.floor(m / 12);
		this._viewMonth = ((m % 12) + 12) % 12;
		this.render();
	};

	Calendar.prototype._canNavigate = function (delta) {
		var probe = new Date(this._viewYear, this._viewMonth + delta, 1);
		if (delta < 0 && this._min) {
			var endOfProbe = new Date(probe.getFullYear(), probe.getMonth() + 1, 0);
			return endOfProbe >= this._min;
		}
		if (delta > 0 && this._max) {
			return probe <= this._max;
		}
		return true;
	};

	Calendar.prototype.render = function () {
		var self = this;
		var y = this._viewYear, m = this._viewMonth;
		var today = atMidnight(new Date());
		var first = new Date(y, m, 1);
		var startOffset = first.getDay();
		var gridStart = new Date(y, m, 1 - startOffset);

		var html = '';
		html += '<div class="ep-cal-header">';
		html += '<button type="button" class="ep-cal-nav" data-nav="-1" aria-label="Previous month"' + (this._canNavigate(-1) ? '' : ' disabled') + '>&#8249;</button>';
		html += '<span class="ep-cal-title">' + MONTHS[m] + ' ' + y + '</span>';
		html += '<button type="button" class="ep-cal-nav" data-nav="1" aria-label="Next month"' + (this._canNavigate(1) ? '' : ' disabled') + '>&#8250;</button>';
		html += '</div>';
		html += '<div class="k-calendar-view"><table role="grid" aria-label="' + MONTHS[m] + ' ' + y + '"><thead><tr>';
		for (var w = 0; w < 7; w++) {
			html += '<th scope="col">' + WEEKDAYS[w] + '</th>';
		}
		html += '</tr></thead><tbody>';
		for (var row = 0; row < 6; row++) {
			html += '<tr>';
			for (var col = 0; col < 7; col++) {
				var d = new Date(gridStart);
				d.setDate(gridStart.getDate() + row * 7 + col);
				var out = d.getMonth() !== m;
				var dis = this.isDisabled(d);
				var cls = 'k-calendar-td';
				if (out) cls += ' k-other-month';
				if (dis) cls += ' k-disabled';
				if (sameDay(d, today)) cls += ' k-today';
				if (sameDay(d, this._value)) cls += ' k-selected';
				var label = d.toDateString();
				if (out) {
					html += '<td class="' + cls + '" aria-hidden="true"></td>';
				} else {
					html += '<td class="' + cls + '" role="gridcell" aria-disabled="' + dis + '" aria-selected="' + sameDay(d, this._value) + '">' +
						'<a class="k-link" role="button" tabindex="' + (dis ? '-1' : '0') + '" data-date="' + d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate() + '" aria-label="' + label + '">' + d.getDate() + '</a></td>';
				}
			}
			html += '</tr>';
		}
		html += '</tbody></table></div>';

		this.$el.addClass('k-calendar ep-cal').html(html);

		this.$el.find('.ep-cal-nav').on('click', function (e) {
			e.preventDefault();
			self.navigate(parseInt(this.getAttribute('data-nav'), 10));
		});

		this.$el.find('td:not(.k-disabled):not(.k-other-month) .k-link').on('click keydown', function (e) {
			if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
			e.preventDefault();
			var parts = this.getAttribute('data-date').split('-');
			self._value = new Date(+parts[0], +parts[1] - 1, +parts[2]);
			self.render();
			if (typeof self.opts.change === 'function') {
				self.opts.change.call(self);
			}
		});
	};

	Calendar.prototype.destroy = function () {
		this.$el.empty().removeClass('k-calendar ep-cal').removeData('kendoCalendar');
	};

	$.fn.kendoCalendar = function (opts) {
		return this.each(function () {
			var $el = $(this);
			var existing = $el.data('kendoCalendar');
			if (existing && existing.destroy) existing.destroy();
			$el.data('kendoCalendar', new Calendar($el, opts));
		});
	};

	// window.kendo presence is checked by the theme before initializing the picker.
	window.kendo = window.kendo || { _epShim: true };
})(window.jQuery);
